/**
 * The text of the platform's own forms — one source of truth.
 *
 * WHY THIS FILE EXISTS. The medical self-declaration's eight questions were
 * written out THREE TIMES in the web app — `MedicalDeclarationModal` (the form an
 * employee actually fills in), `OnboardingTab` (the completed view) and
 * `EmployeeDocumentsSubTab` (the printed record) — and nowhere the API could see
 * them.
 *
 * Measured on 2026-08-29: all copies were still identical, so nothing had gone
 * wrong yet. That is luck rather than design. These are the words an employee is
 * legally asked before declaring a health condition, and every extra copy means a
 * wording change can land in one screen while the others — and the printed
 * declaration held as the record of what was asked — go on saying something else.
 * A record that does not match the question is worse than no record.
 *
 * THERE WERE FOUR, NOT THREE. A first pass found the three web copies and
 * concluded the API could not see the questions at all. That was wrong:
 * `MEDICAL_QUESTION_TEMPLATES` in `packages/database/src/hrSeed.ts` is a fourth,
 * exported from `@jeanieiq/database`, which the API does import. Found from the
 * modal's own header comment, which said so plainly and had been read past. It
 * has zero consumers and is left in place with a pointer here, because
 * `@jeanieiq/database` does not depend on this package and adding that edge to
 * deduplicate an unused constant is more reach than it is worth.
 *
 * IT ALSO UNBLOCKED APPROVAL. `tenant_platform_forms` has no body column, so the
 * register could not read the form and DOC 11 could not be approved. Shane:
 * *"there is no way approving certain documents"*.
 * `DOC-CONTENT-READER-NARROWER-THAN-WRITER-001` named this exactly — the form
 * *"has no body at all — the medical form's eight questions are code"* — and
 * said it needed a decision about where the form's text lives. This is that
 * decision: it lives here, in the shared package both sides already depend on.
 *
 * EACH QUESTION CARRIES ITS ANSWER FIELD, rather than the list's ORDER being the
 * contract. `medical_self_declarations` stores the answers in named columns —
 * `q1Heart`, `q2Back` — and `MedicalDeclarationModal` already paired the two
 * that way. Keeping the pairing means reordering the list re-orders a screen and
 * nothing else; with a bare string array, moving one line would silently
 * re-label every declaration ever signed.
 */

/** A form the platform ships, that a tenant can hold on its document register. */
export type PlatformFormKey = 'medical_self_declaration';

export interface PlatformFormQuestion {
  /** The column on `medical_self_declarations` holding this answer. */
  field: string;
  /** The words the employee is asked. */
  label: string;
}

/** The eight answer columns on `medical_self_declarations`. */
export type MedicalDeclarationField =
  | 'q1Heart'
  | 'q2Back'
  | 'q3Asthma'
  | 'q4Diabetes'
  | 'q5Epilepsy'
  | 'q6Allergies'
  | 'q7Medication'
  | 'q8Other';

/**
 * The question each answer column records, keyed by the column.
 *
 * A RECORD RATHER THAN A LIST, so a caller that needs one question can ask for it
 * by name and have the compiler check both ends. `MedicalDeclarationModal` pairs
 * these with its own `AnswersState` keys; a list would have forced a cast there,
 * and a cast is exactly where a mistyped field name hides.
 *
 * Copied verbatim from the three web copies they replace — byte-identical, so no
 * employee is asked something different from what they were asked yesterday.
 */
export const MEDICAL_QUESTION_TEXT: Record<MedicalDeclarationField, string> = {
  q1Heart: 'Have you ever suffered from any form of heart disease, including high blood pressure?',
  q2Back:
    'Do you have any back, neck, or musculoskeletal condition that may affect manual handling?',
  q3Asthma: 'Do you have asthma or any respiratory condition?',
  q4Diabetes: 'Do you have diabetes or any blood-sugar condition?',
  q5Epilepsy: 'Do you have epilepsy or have you ever had a fit/seizure?',
  q6Allergies: 'Do you have any allergies (food, medication, environmental)?',
  q7Medication: 'Are you currently taking any prescribed medication?',
  q8Other: 'Are there any other medical conditions or considerations we should know about?',
};

/**
 * The same eight, in the order the columns are numbered — for callers that render
 * the whole form, like the register building a document body.
 *
 * Derived from the record above rather than listed again, because two lists is
 * the fault this whole file exists to remove.
 */
export const MEDICAL_SELF_DECLARATION_QUESTIONS: readonly PlatformFormQuestion[] = (
  Object.keys(MEDICAL_QUESTION_TEXT) as MedicalDeclarationField[]
).map((field) => ({ field, label: MEDICAL_QUESTION_TEXT[field] }));

/** Every platform form's questions, by the key `tenant_platform_forms` stores. */
export const PLATFORM_FORM_QUESTIONS: Record<PlatformFormKey, readonly PlatformFormQuestion[]> = {
  medical_self_declaration: MEDICAL_SELF_DECLARATION_QUESTIONS,
};

/**
 * The ISO 9001 clause each platform form answers — DOC-CLAUSE-AUTOFILL-001.
 *
 * IN CODE RATHER THAN IN ADMIN, and that is the right home for this ONE form
 * rather than a shortcut. `tenant_platform_forms` records that a tenant HOLDS a
 * form; the form itself is the platform's, questions and all, and its questions
 * already live here. A clause is a fact about the form, so it belongs beside
 * them. A tenant cannot edit the questions and has no business editing what the
 * form answers either — what they CAN edit is the clause on their own register
 * entry, which is where a tenant-specific answer belongs.
 *
 * It also keeps a promise the register makes: change this and every tenant
 * adopting the form afterwards is tagged, with no per-tenant data entry.
 *
 * ── 7.1.2 IS THE HONEST SECOND-BEST, NOT A CONFIDENT ANSWER ──────────────
 *
 * Named as weak so the next reader questions it first, the same way the one-off
 * tagging script named its three weakest mappings.
 *
 * 7.1.2 People asks the organisation to determine and provide the persons
 * necessary for the QMS. A fitness-to-work declaration speaks to whether the
 * people provided are suitable for the work, which is the closest 9001 comes to
 * it. It is also the clause already on this document where it has been adopted,
 * so nothing moves underneath anybody.
 *
 * The truthful caveat: a medical declaration is more naturally an ISO 45001
 * health-and-safety artefact than a 9001 quality one. Under 45001 it would sit
 * near 6.1 hazard identification. When this tenant's other standards are built
 * out, revisit this rather than assuming 7.1.2 was ever the strong answer.
 *
 * A form the platform does not recognise gets null, not a guess — same rule as
 * the questions below.
 */
export const PLATFORM_FORM_CLAUSE: Record<PlatformFormKey, string | null> = {
  medical_self_declaration: '7.1.2',
};

/** The ISO clause a form answers, or null when the platform does not know it. */
export function platformFormClause(formKey: string): string | null {
  return PLATFORM_FORM_CLAUSE[formKey as PlatformFormKey] ?? null;
}

/**
 * The questions for a form key, or null if the platform does not know that key.
 *
 * NULL RATHER THAN AN EMPTY ARRAY, deliberately. The register uses this to build
 * a document body, and an empty array would render a form with no questions on
 * it — a blank controlled document that looks complete. "I do not know this
 * form" and "this form asks nothing" must not look the same.
 */
export function platformFormQuestions(formKey: string): readonly PlatformFormQuestion[] | null {
  const known = PLATFORM_FORM_QUESTIONS[formKey as PlatformFormKey];
  return known && known.length > 0 ? known : null;
}
